import React, { useEffect, useState, useCallback } from 'react';
import { KeyRound, Plus, Pencil, Trash2, X, Check, ChevronDown, ChevronUp, UserPlus, Monitor, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

const LICENSE_TYPES = [
  { value: 'per_user',    label: 'Por usuario' },
  { value: 'per_device',  label: 'Por dispositivo' },
  { value: 'per_core',    label: 'Por core/CPU' },
  { value: 'site',        label: 'Site license' },
  { value: 'concurrent',  label: 'Concurrente' },
];

const CURRENCIES = ['USD','ARS','EUR','BRL','CLP','COP','MXN'];

function fmtMoney(n, cur = 'USD') {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(n || 0);
}

function SeatsBar({ used, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const color = pct >= 100 ? 'var(--red)' : pct >= 80 ? 'var(--amber)' : 'var(--green)';
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
        <span style={{ color: 'var(--ink-2)' }}>{used}/{total} seats</span>
        <span style={{ fontWeight: 600, color }}>{total - used} disponibles</span>
      </div>
      <div style={{ height: 6, background: 'var(--border)', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 6 }} />
      </div>
    </div>
  );
}

function LicenseForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial || {
    software_name: '', vendor: '', license_type: 'per_user', total_seats: 1,
    cost_per_seat: 0, currency: 'USD', frequency: 'monthly',
    purchase_date: '', expiry_date: '', vendor_contract: '', invoice_number: '', notes: '',
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  return (
    <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 10, padding: 20, marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>{initial ? 'Editar licencia' : 'Nueva licencia'}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div style={{ gridColumn: '1/3' }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Software *</label>
          <input className="form-input" placeholder="ej: Microsoft Office 365" value={f.software_name} onChange={e => set('software_name', e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Proveedor</label>
          <input className="form-input" placeholder="ej: Microsoft" value={f.vendor} onChange={e => set('vendor', e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Tipo de licencia</label>
          <select className="form-input" value={f.license_type} onChange={e => set('license_type', e.target.value)}>
            {LICENSE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Cantidad de seats *</label>
          <input className="form-input" type="number" min="1" value={f.total_seats} onChange={e => set('total_seats', e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Costo por seat</label>
          <input className="form-input" type="number" min="0" step="0.01" placeholder="0.00" value={f.cost_per_seat} onChange={e => set('cost_per_seat', e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Moneda</label>
          <select className="form-input" value={f.currency} onChange={e => set('currency', e.target.value)}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Frecuencia</label>
          <select className="form-input" value={f.frequency} onChange={e => set('frequency', e.target.value)}>
            <option value="monthly">Mensual</option>
            <option value="annual">Anual</option>
            <option value="one_time">Unico</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Fecha de compra</label>
          <input className="form-input" type="date" value={f.purchase_date} onChange={e => set('purchase_date', e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Vencimiento</label>
          <input className="form-input" type="date" value={f.expiry_date} onChange={e => set('expiry_date', e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Nro. contrato</label>
          <input className="form-input" placeholder="CONT-2024-001" value={f.vendor_contract} onChange={e => set('vendor_contract', e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Nro. factura</label>
          <input className="form-input" placeholder="FAC-0001234" value={f.invoice_number} onChange={e => set('invoice_number', e.target.value)} />
        </div>
        <div style={{ gridColumn: '1/4' }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Notas</label>
          <input className="form-input" placeholder="Observaciones..." value={f.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </div>
      <div style={{ background: 'var(--accent-dim)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--accent)', marginBottom: 12 }}>
        Costo total: <strong>{fmtMoney(parseFloat(f.cost_per_seat || 0) * parseInt(f.total_seats || 1), f.currency)}</strong> / {f.frequency === 'monthly' ? 'mes' : f.frequency === 'annual' ? 'ano' : 'unico'}
        {f.frequency === 'annual' && ` → ${fmtMoney(parseFloat(f.cost_per_seat || 0) * parseInt(f.total_seats || 1) / 12, f.currency)}/mes`}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancelar</button>
        <button className="btn btn-primary btn-sm" onClick={() => onSave(f)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Check size={13} /> Guardar
        </button>
      </div>
    </div>
  );
}

function AssignForm({ licenseId, headers, onAssigned, onCancel }) {
  const [assets, setAssets] = useState([]);
  const [form, setForm] = useState({ user_name: '', asset_id: '', notes: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    fetch(`${API_URL}/assets`, { headers }).then(r => r.json()).then(d => setAssets(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!form.user_name.trim() && !form.asset_id) { alert('Ingrese usuario o seleccione un equipo'); return; }
    const r = await fetch(`${API_URL}/licenses/${licenseId}/assignments`, {
      method: 'POST', headers, body: JSON.stringify(form),
    });
    const data = await r.json();
    if (r.ok) onAssigned(data);
    else alert(data.error || 'Error al asignar');
  };

  return (
    <div style={{ background: 'var(--green-dim)', border: '1px solid #86efac', borderRadius: 8, padding: 14, marginBottom: 12 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: 'var(--green)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <UserPlus size={14} /> Asignar licencia
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Usuario</label>
          <input className="form-input" placeholder="ej: Juan Perez" value={form.user_name} onChange={e => set('user_name', e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Equipo (opcional)</label>
          <select className="form-input" value={form.asset_id} onChange={e => set('asset_id', e.target.value)}>
            <option value="">Sin equipo asignado</option>
            {assets.map(a => <option key={a.id} value={a.id}>{a.hostname}{a.user_name ? ` (${a.user_name})` : ''}</option>)}
          </select>
        </div>
        <div style={{ gridColumn: '1/3' }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Notas</label>
          <input className="form-input" placeholder="Observaciones..." value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancelar</button>
        <button className="btn btn-primary btn-sm" onClick={handleSubmit} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Check size={13} /> Asignar
        </button>
      </div>
    </div>
  );
}

function LicenseCard({ lic, headers, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const today = new Date();
  const expired = lic.expiry_date && new Date(lic.expiry_date) < today;
  const expiringSoon = lic.expiry_date && !expired && new Date(lic.expiry_date) <= new Date(today.getTime() + 60*24*60*60*1000);
  const overused = lic.used_seats > lic.total_seats;

  const loadDetail = async () => {
    const r = await fetch(`${API_URL}/licenses/${lic.id}`, { headers });
    const d = await r.json();
    setDetail(d);
  };

  const handleExpand = () => {
    if (!expanded) loadDetail();
    setExpanded(!expanded);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Eliminar licencia "${lic.software_name}" y todas sus asignaciones?`)) return;
    await fetch(`${API_URL}/licenses/${lic.id}`, { method: 'DELETE', headers });
    onRefresh();
  };

  const handleUnassign = async (assignId) => {
    if (!window.confirm('Quitar esta asignacion?')) return;
    await fetch(`${API_URL}/licenses/assignments/${assignId}`, { method: 'DELETE', headers });
    loadDetail();
    onRefresh();
  };

  const handleEdit = async (form) => {
    const r = await fetch(`${API_URL}/licenses/${lic.id}`, { method: 'PUT', headers, body: JSON.stringify(form) });
    if (r.ok) { setEditing(false); onRefresh(); }
    else { const d = await r.json(); alert(d.error || 'Error'); }
  };

  const borderColor = expired ? 'var(--red)' : overused ? 'var(--amber)' : expiringSoon ? 'var(--amber)' : 'var(--border)';

  return (
    <div style={{ border: `1px solid ${borderColor}`, borderRadius: 10, marginBottom: 12, overflow: 'hidden', background: 'var(--surface)' }}>
      {editing ? (
        <div style={{ padding: 16 }}>
          <LicenseForm
            initial={{ ...lic, purchase_date: lic.purchase_date?.split('T')[0]||'', expiry_date: lic.expiry_date?.split('T')[0]||'' }}
            onSave={handleEdit}
            onCancel={() => setEditing(false)}
          />
        </div>
      ) : (
        <>
          {/* Header */}
          <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={handleExpand}>
            <div style={{ width: 36, height: 36, background: 'var(--accent-dim)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <KeyRound size={16} style={{ color: 'var(--accent)' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink)' }}>{lic.software_name}</div>
                {expired && <span style={{ color: 'var(--red)', fontSize: 10, fontWeight: 600,}}>VENCIDA</span>}
                {expiringSoon && <span style={{ color: 'var(--amber)', fontSize: 10, fontWeight: 600,}}>VENCE PRONTO</span>}
                {overused && <span style={{ color: 'var(--amber)', fontSize: 10, fontWeight: 600,}}>EXCEDIDA</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                {lic.vendor && <span>{lic.vendor} · </span>}
                <span>{LICENSE_TYPES.find(t => t.value === lic.license_type)?.label}</span>
                {lic.expiry_date && <span> · Vence {new Date(lic.expiry_date).toLocaleDateString('es-AR')}</span>}
              </div>
            </div>
            <div style={{ textAlign: 'right', minWidth: 140 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>{fmtMoney(lic.monthly_cost, lic.currency)}/mes</div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{fmtMoney(lic.cost_per_seat, lic.currency)}/seat</div>
            </div>
            <div style={{ minWidth: 160 }}>
              <SeatsBar used={lic.used_seats} total={lic.total_seats} />
            </div>
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
              <button onClick={() => setEditing(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-2)', padding: 4 }}><Pencil size={14} /></button>
              <button onClick={handleDelete} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', padding: 4 }}><Trash2 size={14} /></button>
            </div>
            {expanded ? <ChevronUp size={16} style={{ color: 'var(--ink-3)', flexShrink: 0 }} /> : <ChevronDown size={16} style={{ color: 'var(--ink-3)', flexShrink: 0 }} />}
          </div>

          {/* Expandido */}
          {expanded && (
            <div style={{ borderTop: '1px solid var(--bg)', padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>
                  Asignaciones ({detail?.used_seats || 0} de {lic.total_seats})
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => setAssigning(!assigning)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }} disabled={lic.available_seats <= 0}>
                  <UserPlus size={13} /> Asignar
                </button>
              </div>

              {assigning && (
                <AssignForm licenseId={lic.id} headers={headers}
                  onAssigned={() => { setAssigning(false); loadDetail(); onRefresh(); }}
                  onCancel={() => setAssigning(false)} />
              )}

              {!detail ? (
                <div style={{ textAlign: 'center', padding: 20 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
              ) : detail.assignments?.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: 'var(--ink-3)', fontSize: 13 }}>Sin asignaciones todavia</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-subtle)' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)', borderBottom: '1px solid var(--border)' }}>Usuario</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)', borderBottom: '1px solid var(--border)' }}>Equipo</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)', borderBottom: '1px solid var(--border)' }}>Asignado</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)', borderBottom: '1px solid var(--border)' }}>Notas</th>
                      <th style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.assignments.map(a => (
                      <tr key={a.id} style={{ borderBottom: '1px solid var(--bg)' }}>
                        <td style={{ padding: '8px 10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 26, height: 26, background: 'var(--accent-dim)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent)' }}>
                              {(a.user_name || '?')[0].toUpperCase()}
                            </div>
                            <span style={{ fontWeight: 500 }}>{a.user_name || '—'}</span>
                          </div>
                        </td>
                        <td style={{ padding: '8px 10px', color: 'var(--ink-2)' }}>
                          {a.hostname ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Monitor size={12} /> {a.hostname}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '8px 10px', color: 'var(--ink-2)', fontSize: 12 }}>
                          {new Date(a.assigned_at).toLocaleDateString('es-AR')}
                        </td>
                        <td style={{ padding: '8px 10px', color: 'var(--ink-2)', fontSize: 12 }}>{a.notes || '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                          <button onClick={() => handleUnassign(a.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', padding: 4 }} title="Quitar asignacion">
                            <X size={13} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function Licenses() {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}/licenses`, { headers }).then(r => r.json())
      .then(d => setLicenses(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    const r = await fetch(`${API_URL}/licenses`, { method: 'POST', headers, body: JSON.stringify(form) });
    if (r.ok) { setShowForm(false); load(); }
    else { const d = await r.json(); alert(d.error || 'Error'); }
  };

  const filtered = licenses.filter(l =>
    !search || l.software_name.toLowerCase().includes(search.toLowerCase()) ||
    (l.vendor || '').toLowerCase().includes(search.toLowerCase())
  );

  // Stats
  const totalMonthly = licenses.reduce((s, l) => s + (l.monthly_cost || 0), 0);
  const totalSeats   = licenses.reduce((s, l) => s + l.total_seats, 0);
  const usedSeats    = licenses.reduce((s, l) => s + l.used_seats, 0);
  const alerts       = licenses.filter(l => {
    const exp = l.expiry_date && new Date(l.expiry_date) <= new Date(Date.now() + 60*24*60*60*1000);
    const over = l.used_seats > l.total_seats;
    return exp || over;
  }).length;

  const firstCurrency = licenses[0]?.currency || 'USD';

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div>
          <div className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <KeyRound size={22} style={{ opacity: 0.7 }} /> Licencias de software
          </div>
          <div className="page-sub">Gestion y asignacion de licencias</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={15} /> Nueva licencia
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'COSTO MENSUAL', value: fmtMoney(totalMonthly, firstCurrency), sub: 'total licencias', color: 'var(--accent-dim)', border: 'var(--accent-dim)', text: '#1e40af' },
          { label: 'LICENCIAS', value: licenses.length, sub: 'productos distintos', color: 'var(--green-dim)', border: 'var(--green-dim)', text: 'var(--green)' },
          { label: 'SEATS USADOS', value: `${usedSeats}/${totalSeats}`, sub: `${totalSeats - usedSeats} disponibles`, color: 'var(--amber-dim)', border: 'var(--amber-dim)', text: 'var(--amber)' },
          { label: 'ALERTAS', value: alerts, sub: 'vencimientos / excedidas', color: alerts > 0 ? 'var(--red-dim)' : 'var(--bg-subtle)', border: alerts > 0 ? 'var(--red-dim)' : 'var(--border)', text: alerts > 0 ? 'var(--red)' : 'var(--ink-2)' },
        ].map(s => (
          <div key={s.label} style={{ background: s.color, border: `1px solid ${s.border}`, borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: s.text, marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.text }}>{s.value}</div>
            <div style={{ fontSize: 11, color: s.text, opacity: 0.7 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Formulario nueva licencia */}
      {showForm && <LicenseForm onSave={handleCreate} onCancel={() => setShowForm(false)} />}

      {/* Buscador */}
      <div className="card" style={{ padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
        <input className="form-input" placeholder="Buscar por software o proveedor..."
          value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>{filtered.length} licencia{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--ink-3)' }}>
          {search ? 'Sin resultados para la busqueda.' : 'No hay licencias registradas. Crea la primera.'}
        </div>
      ) : (
        filtered.map(lic => (
          <LicenseCard key={lic.id} lic={lic} headers={headers} onRefresh={load} />
        ))
      )}
    </div>
  );
}
